/**
 * The visitor funnel id: one random v4 UUID per visitor, sent on the eligibility check, enrollment
 * and consult-booked calls so the analytics sink can join a check to the enrollment and booking that
 * follow. Minted for every visitor, attributed or organic. Persisted in localStorage for 30 days —
 * random, so non-PHI; this browser profile and origin only. Shape: `@nrx/betty-contracts/attribution`.
 */

import { parseFunnelId } from '@nrx/betty-contracts/attribution';

export const FUNNEL_STORAGE_KEY = 'ffs.funnel.v1';

const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

interface FunnelRecord {
  readonly id: string;
  readonly createdAt: string;
}

let memory: FunnelRecord | undefined;

export function resetFunnelIdForTests(): void {
  memory = undefined;
}

/** A malformed or tampered value (from any app version) is ignored rather than sent. */
function toRecord(raw: unknown): FunnelRecord | undefined {
  if (typeof raw !== 'object' || raw === null) return undefined;
  const { id, createdAt } = raw as { id?: unknown; createdAt?: unknown };
  const validId = parseFunnelId(id);
  if (validId === undefined || typeof createdAt !== 'string') return undefined;
  return { id: validId, createdAt };
}

function isLive(record: FunnelRecord, now: Date): boolean {
  const age = now.getTime() - Date.parse(record.createdAt);
  // NaN (unparseable) and negative (a future timestamp) both fail, so neither can live forever.
  return age >= 0 && age < MAX_AGE_MS;
}

function readLive(now: Date): FunnelRecord | undefined {
  // Memory first: it is newer than storage whenever `setItem` threw (a full quota), and it
  // keeps this tab's id stable if another tab rotated storage.
  if (memory !== undefined && isLive(memory, now)) return memory;
  try {
    const raw = localStorage.getItem(FUNNEL_STORAGE_KEY);
    const stored = raw === null ? undefined : toRecord(JSON.parse(raw));
    return stored !== undefined && isLive(stored, now) ? stored : undefined;
  } catch {
    return undefined;
  }
}

function mint(now: Date): FunnelRecord | undefined {
  try {
    // A polyfilled randomUUID that breaks the contract would only be dropped by the service.
    const id = parseFunnelId(crypto.randomUUID());
    return id === undefined ? undefined : { id, createdAt: now.toISOString() };
  } catch {
    // No randomUUID (an old browser, an insecure context): no funnel id beats a failed boot.
    return undefined;
  }
}

function write(record: FunnelRecord): void {
  memory = record;
  try {
    localStorage.setItem(FUNNEL_STORAGE_KEY, JSON.stringify(record));
  } catch {
    // Storage blocked (private mode, disabled site data): the in-memory copy covers this page load.
  }
}

/**
 * The visitor's funnel id: reused while valid and under 30 days old, minted otherwise. Called at boot
 * and again for each funnel request, so a window that lapses mid-visit rotates cleanly. Never
 * throws; `undefined` only when the browser cannot mint a UUID.
 */
export function ensureFunnelId(now: Date = new Date()): string | undefined {
  const live = readLive(now);
  if (live !== undefined) {
    memory = live;
    return live.id;
  }
  const fresh = mint(now);
  if (fresh === undefined) return undefined;
  write(fresh);
  return fresh.id;
}
